// Notes: everything the account has written down, in one list.
//
// TWO KINDS OF NOTE, ONE SCREEN, which is what the tab has been promising since
// the shell landed ("both on their own and attached to the things on your day").
// A note of its own is a row in `note`; a note written ON something is
// `routine.note`, which the day screen has drawn a glyph for all along and which
// had, until this screen, no way to be read except by opening the item it sits
// on. The server sends them merged, ordered and searched together and marks each
// row with its `kind`; this draws them and sends each write where that row lives.
//
// THE LIST IS BLOCKS NOW - a pinned area, the sections somebody made, the
// automatic Reminder and Schedules blocks, then everything in neither. Three
// things about that are
// load-bearing and read as arbitrary:
//
//   AN ACCOUNT WITH ONLY FREE-STANDING NOTES, NO SECTIONS AND NOTHING PINNED
//   SEES THE LIST IT ALWAYS HAD. No headings, no "Not in a section" over
//   everything, no empty organiser to dismiss. `groupNotes` returns one block
//   for that account and `showsGroups` is what this file asks before it draws a
//   single header. Routine notes intentionally add their automatic origin
//   headings even when the account has made no section of its own.
//
//   DRAG IS NEVER THE ONLY WAY. Every row carries a section picker - a real
//   `<select>` - and a pin button, so grouping and pinning work from the
//   keyboard, from a screen reader and on a touch screen, where HTML5 drag and
//   drop does not fire at all. Dragging is the shortcut, not the mechanism. No
//   drag library: `dragstart` and `drop` are four handlers and this client's
//   dependency budget is deliberately small and flat.
//
//   A FOLDED BLOCK MAY NOT HOLD A ROW IT DID NOT HOLD BEFORE. Pinning a note
//   while the pinned area is folded, or moving one into a folded section, would
//   otherwise be a write whose result is invisible - which reads as a failure
//   and is done again (web/src/README.md), and after "did I just lose that?"
//   the next thing tried is writing it out a second time. `openWhatGrew` below
//   is that rule, stated about what a block CONTAINS rather than about who
//   wrote it, and it is why the fold state lives here rather than inside each
//   block.
//
// THE NEW-NOTE BUTTON IS IN THE TOP ROW, at every width, and is deliberately not
// a floating +. The `.fab` is hidden at sidebar width, and the shell's own add
// action belongs to the day - it goes to Today and opens the add sheet, which is
// the right thing for a schedule and the wrong thing entirely for a note. An
// empty state that names a button that is not on screen at that width is the
// exact defect this client has already shipped once (web/src/README.md).
//
// A SEARCH CAN HIDE WHAT YOU JUST WROTE, and that is this screen's version of
// "a write that lands outside the day on screen". Leaving the editor says what
// was saved, and says when the search you have typed does not show it - because
// a note that was written and then appears nowhere is indistinguishable from one
// that failed to save, and the answer to that is to write it again.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { IsoDay, Note, NoteSection } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { hueIn } from '../edit/edit-model.ts';
import { AccountButton } from '../shell/NavBar.tsx';
import type { Mode } from '../shell/theme.ts';
import { useMode } from '../shell/theme.ts';
import { useVoice, wording } from '../shell/voice.ts';
import { NoteEditor } from './NoteEditor.tsx';
import { readFolded, toggleFolded, writeFolded } from './folded.ts';
import {
  canOrganise,
  groupNotes,
  LOOSE_KEY,
  PINNED_KEY,
  REMINDER_KEY,
  SCHEDULES_KEY,
  previewOf,
  showsGroups,
  whenLabel,
  whereLabel,
} from './note-model.ts';
import type { NoteGroup } from './note-model.ts';
import { useNotes } from './useNotes.ts';

interface Props {
  say: (message: string, undo?: () => void, retire?: () => void) => void;
  amendNotice: (undo: () => void, message: string) => boolean;
  dismiss: (undo?: () => void) => void;
  shareRevision: number;
  pendingShareStatus: ReadonlyMap<string, boolean>;
  onShareStatusRead: (revision: number, observed: ReadonlyMap<string, boolean>) => void;
  onShareChanged: (noteId: string, shared: boolean) => void;
  routineNoteRevision: number;
  onRoutineNoteChanged: () => void;
  /** Any 401, from this screen's own read or from any write it makes. */
  onSignedOut: () => void;
  onSettings: () => void;
  /**
   * Realize, the mind mapper, which is a page inside this tab (captain,
   * 2026-08-19) rather than a tab of its own.
   */
  onOpenRealize: () => void;
}

/** The longest name a section may have - the column's own bound, and the API's. */
const NAME_MAX = 60;

/** One row, told apart the way the list itself keys them: an item note and a note may share an id. */
function rowKey(note: Note): string {
  return `${note.kind}-${note.id}`;
}

/**
 * Which FOLDED blocks are now holding a row they were not holding last time.
 *
 * `seen` is the previous observation, block by block. A KEY WITH NO REMEMBERED
 * SET IS A BASELINE, NEVER A REVEAL - which is what stops every folded block
 * springing open the moment the first payload lands - and the baseline is the
 * WHOLE SCREEN rather than each key on its own: while nothing at all has been
 * observed, nothing can have grown. Once something has, a block appearing for
 * the first time HAS gained every row in it, which is exactly the case of
 * pinning a note back into a pinned area that was folded before it emptied.
 */
function openWhatGrew(
  groups: readonly NoteGroup[],
  seen: ReadonlyMap<string, ReadonlySet<string>>,
  folded: ReadonlySet<string>,
): string[] {
  if (seen.size === 0) return [];
  return groups
    .filter((group) => {
      if (!folded.has(group.key)) return false;
      const before = seen.get(group.key);
      return before === undefined || group.notes.some((note) => !before.has(rowKey(note)));
    })
    .map((group) => group.key);
}

export function NotesScreen({
  say,
  amendNotice,
  dismiss,
  shareRevision,
  pendingShareStatus,
  onShareStatusRead,
  onShareChanged,
  routineNoteRevision,
  onRoutineNoteChanged,
  onSignedOut,
  onSettings,
  onOpenRealize,
}: Props) {
  const notes = useNotes({
    say,
    amendNotice,
    dismiss,
    signOut: onSignedOut,
    shareRevision,
    pendingShareStatus,
    onShareStatusRead,
    routineNoteRevision,
    onRoutineNoteChanged,
  });
  const { editing, search, deleted, sections, archivedSections } = notes;

  // WHICH deleted row is asking, held here rather than inside the row it is
  // asked on. Permanent deletion is the one act in this product with no undo, so
  // the question may not outlive the moment it was put: a row keyed by its id is
  // re-rendered rather than remounted, so state kept inside it stays armed for
  // the life of the screen, and the next press of a red button somebody read as
  // "start the delete" destroys a note. One row at a time, and put down again by
  // anything that moves the screen on - a change to the search, a re-read of the
  // list, which is what `deleted` changing identity means.
  //
  // Notes and sections share the one slot and are told apart by a prefix, so
  // arming a section cannot leave a note armed behind it.
  const [arming, setArming] = useState<string | null>(null);
  useEffect(() => {
    setArming(null);
  }, [search, deleted, archivedSections]);

  // The stored hex is the light one; on a dark page a row paints the prototype's
  // dark counterpart instead (`edit/edit-model.ts`). Read once here rather than
  // in each row: this list has no cap on it.
  const mode = useMode();

  // Which blocks are folded. Read from storage ONCE, on mount - the value is
  // this browser's (`folded.ts`), and re-reading it on every render would fight
  // whatever this screen has just set.
  const [folded, setFolded] = useState<ReadonlySet<string>>(readFolded);

  // What may be folded AT ALL, which is the account's blocks rather than the
  // ones currently drawn. Pruning the stored value against the drawn set would
  // forget every choice about a group the search happens to be hiding.
  const knownKeys = useMemo(
    () =>
      new Set<string>([
        PINNED_KEY,
        REMINDER_KEY,
        SCHEDULES_KEY,
        LOOSE_KEY,
        ...sections.map((section) => section.id),
      ]),
    [sections],
  );

  const remember = useCallback(
    (next: ReadonlySet<string>) => {
      setFolded(next);
      writeFolded(next, knownKeys);
    },
    [knownKeys],
  );

  const list = notes.notes;
  // From the term the LIST came back with, never from the box. The two disagree
  // for a debounce plus a round trip after every keystroke, so a sentence drawn
  // from the box describes a screen that is not there: clear a search that
  // matched nothing and the box empties at once while the empty list stays,
  // which would put "Nothing written down yet." over an account full of notes,
  // and swap the button under the cursor while it did. Transient when the next
  // read lands, durable when it fails and the old list is kept on purpose.
  const searching = notes.listedFor.trim() !== '';

  // Memoised so that the rule below runs when the BLOCKS move and not when a
  // drag does: `dragging` and `over` change many times a second while a row is
  // in the air, and re-deriving the blocks then would set the rule comparing
  // against itself.
  const groups = useMemo(
    () => (list === null ? [] : groupNotes(list, sections, searching)),
    [list, sections, searching],
  );
  const grouped = showsGroups(groups);

  // What each drawn block held when it was last looked at. A ref rather than
  // state: it is the previous observation, so writing it must not itself be a
  // reason to render.
  const seen = useRef<ReadonlyMap<string, ReadonlySet<string>>>(new Map());

  /**
   * OPEN ANY FOLDED BLOCK THAT HAS GAINED A ROW. The invariant, made true by
   * construction rather than by every caller remembering it.
   *
   * WHY IT LIVES HERE AND NOT AT THE WRITES. It was five `reveal(key)` calls,
   * opt-in at each site, and it held only where somebody had thought of it:
   * unpinning a note back into a folded section and archiving a section into a
   * folded "Not in a section" both dropped rows out of sight, and every write
   * added later would have started out forgetting too. Stated as a property of
   * what a folded block is allowed to CONTAIN it needs no cooperation from any
   * caller - it covers the five writes that exist, the ones that do not exist
   * yet, and the writes made from outside this screen entirely, such as saving
   * a note in the editor, which no per-call-site guard could have reached.
   *
   * IT IS ALSO MORE HONEST. Nothing here updates optimistically: every write in
   * `useNotes` ends in a `refresh()`, so a row changes block only when a new
   * payload arrives and `groups` is recomputed. The old calls were PREDICTING
   * where the server would put the row; this reads where it actually went.
   */
  useEffect(() => {
    // Under a search every block is drawn open and the choice is suspended, so
    // comparing here would spend it on churn from the search term rather than
    // on a write. The ref is dropped instead, so the first render after the box
    // clears baselines again rather than reading typing as growth.
    if (searching) {
      seen.current = new Map();
      return;
    }
    if (list === null) return;

    const grew = openWhatGrew(groups, seen.current, folded);
    seen.current = new Map(
      groups.map((group) => [group.key, new Set(group.notes.map(rowKey))] as const),
    );

    // Only when something actually has to open, or this would set state on
    // every pass and never settle.
    if (grew.length === 0) return;
    const kept = new Set(folded);
    for (const key of grew) kept.delete(key);
    remember(kept);
  }, [groups, folded, searching, list, remember]);

  // Which note is being dragged, and which block it is over. Held here because a
  // drag is a conversation between two rows that are not each other's children.
  const [dragging, setDragging] = useState<Note | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const { moveNote, setPinned } = notes;

  // None of these three opens anything: where the row lands is read off the
  // next payload by the rule above, which is the only place that knows.
  const onPin = useCallback(
    (note: Note, pinned: boolean) => {
      void setPinned(note, pinned);
    },
    [setPinned],
  );

  /**
   * The PICKER's move: the section changes and the pin does not.
   *
   * Choosing "Work" for a note that is pinned leaves it pinned, because the
   * control is about which section it is in and says nothing about the pin. A
   * DROP is the other case and is `onDropIn` below.
   */
  const onMove = useCallback(
    (note: Note, sectionId: string | null) => {
      void moveNote(note, sectionId);
    },
    [moveNote],
  );

  /**
   * What a drop on one block does. The pinned area pins; a section files.
   *
   * Dropping a PINNED note onto a section unpins it in the same act, which the
   * picker above deliberately does not: the row visibly leaves the pinned area
   * on its way, so leaving it pinned would spring it straight back to the top
   * and read as a drag that failed. It travels as ONE patch (`moveNote`'s
   * `unpin`), not two writes - see the note there.
   */
  const onDropIn = useCallback(
    (group: NoteGroup, note: Note) => {
      setDragging(null);
      setOver(null);
      if (group.kind === 'pinned') {
        onPin(note, true);
        return;
      }
      void moveNote(note, group.section?.id ?? null, true);
    },
    [moveNote, onPin],
  );

  if (editing !== null) {
    const shareStatus =
      editing.kind === 'open' && editing.note.kind === 'note'
        ? (pendingShareStatus.get(editing.note.id) ??
          list?.find((note) => note.kind === 'note' && note.id === editing.note.id)?.shared ??
          editing.note.shared)
        : undefined;
    return (
      <NoteEditor
        // Keyed by the row it is about, so opening a second note after a first
        // replaces the editor rather than reusing one still holding the
        // previous note's draft in its own state.
        key={editing.kind === 'open' ? editing.note.id : 'new'}
        note={editing.kind === 'open' ? editing.note : null}
        onClose={notes.close}
        onDelete={notes.remove}
        onSignedOut={onSignedOut}
        say={say}
        // Making or turning off a read-only link changes what the LIST says
        // about the note - every published one carries a badge - and it changes
        // not one character of the note, so it would never arrive through
        // `onClose`'s `saved`. A write that the screen behind it draws has to
        // tell that screen (AGENTS.md, the board's `refresh()`).
        onShareChanged={onShareChanged}
        onRoutineNoteChanged={onRoutineNoteChanged}
        onRoutineNoteSaved={notes.routineNoteSaved}
        onRoutineNoteSaveFailed={notes.routineNoteSaveFailed}
        onRoutineNoteCleared={notes.routineNoteCleared}
        shareStatus={shareStatus}
      />
    );
  }

  return (
    <div className="scroll">
      <div className="wrap">
        <div className="toprow">
          <h1>Notes</h1>
          {/* At every width, which is the whole reason it is here rather than
              in a floating button. */}
          <button className="btn primary" onClick={notes.openNew}>
            <Icon name="plus" />
            New note
          </button>
          <AccountButton onClick={onSettings} />
        </div>

        <div className="searchbox">
          <Icon name="search" />
          <input
            // `type="search"` so a phone offers the right keyboard. The
            // browser's OWN clear control is suppressed in the stylesheet and
            // replaced by the button below: WebKit draws its as a blue cross
            // that belongs to no theme in this app, and it is absent in Firefox
            // altogether - so the one control that empties this box would look
            // wrong on one browser and not exist on another.
            type="search"
            placeholder="Search notes"
            aria-label="Search notes"
            value={notes.search}
            onChange={(event) => notes.setSearch(event.target.value)}
          />
          {notes.search !== '' && (
            <button
              className="searchclear"
              aria-label="Clear search"
              onClick={() => notes.setSearch('')}
            >
              <Icon name="x" />
            </button>
          )}
        </div>

        {/* THE DOOR TO REALIZE, above the list and below the search, at every
            width. A row rather than a button in the top row: that row is already
            a heading, a New note and the account button on a 360px phone, and a
            fourth control there would push one of them off. A row also lets it
            say what is behind it, which "Realize" alone does not - it is a word
            the product invented, so the first person to see it has to be told.

            It is NOT filtered by the search box above it: the search is over
            notes, and a door that came and went as you typed would be a door
            nobody could rely on finding. */}
        <button className="rzentry" onClick={onOpenRealize}>
          <Icon name="realize" />
          <span className="rze-t">
            <b>Realize</b>
            <span>Mind maps - a topic in the middle, and branches off it</span>
          </span>
          <Icon name="chevR" />
        </button>

        {list === null && notes.problem === null && (
          <p className="sheetwait" role="status">
            Loading…
          </p>
        )}

        {/* `groups.length === 0` and not just an empty list: an account with a
            section and nothing written yet gets that section's own heading and
            its "nothing in here" line, which says the same thing in the place it
            is about. The tall empty state above a row of headings would push
            them off the screen to say it twice. */}
        {list !== null && list.length === 0 && groups.length === 0 && (
          <EmptyNotes
            searching={searching}
            anyDeleted={notes.deleted.length > 0}
            onClear={() => notes.setSearch('')}
            onNew={notes.openNew}
          />
        )}

        {/* Drawn whenever there is a block to draw, which includes an empty
            section on an account with nothing written: a section nobody can see
            is a section nobody can rename or delete. */}
        {groups.map((group) => (
          <NoteGroupView
            key={group.key}
            group={group}
            grouped={grouped}
            today={notes.today}
            mode={mode}
            sections={sections}
            // While a search is on, EVERY BLOCK IS OPEN and no fold control is
            // drawn. A collapsed block with a match inside it is a heading, a
            // count and no rows - somebody's note hidden by a control they set
            // days ago, which is the search failing at the one thing it is for.
            // The remembered choice is untouched and comes back when the box is
            // cleared.
            //
            // AND GATED ON `grouped` FOR THE SAME KIND OF REASON: the plain
            // block is drawn with no header, so it has no fold control - and a
            // block that cannot be unfolded must never be able to arrive
            // folded. `loose` stays in the remembered set after the last
            // section is deleted or the last note unpinned, and without this
            // the whole list would come back `inert` with nothing collapsed to
            // show for it: a screen that is visible, dead, and has no control
            // on it to undo that.
            folded={grouped && !searching && folded.has(group.key)}
            foldable={!searching}
            onToggleFold={() => remember(toggleFolded(folded, group.key))}
            dragging={dragging}
            over={over === group.key}
            onOver={() => setOver(group.key)}
            onLeave={() => setOver((key) => (key === group.key ? null : key))}
            onDrop={(note) => onDropIn(group, note)}
            onDragNote={setDragging}
            onDragEnd={() => {
              setDragging(null);
              setOver(null);
            }}
            onOpen={notes.open}
            onPin={onPin}
            onMove={onMove}
            onRename={(name) => void notes.renameSection(group.section!, name)}
            onPinSection={(pinned) => void notes.pinSection(group.section!, pinned)}
            onRemoveSection={() => void notes.removeSection(group.section!)}
          />
        ))}

        {/* The read failed and whatever was last on screen is still there, so
            this says which of the two you are looking at rather than replacing
            a list that may still be right. */}
        {notes.problem !== null && (
          <p className="sheetproblem" role="alert">
            {list === null ? notes.problem : `This list may be out of date. ${notes.problem}`}
          </p>
        )}

        {/* "no formatting" was true until 2026-08-19 and is not any more: notes
            understand a small markdown vocabulary, and a sentence on the list
            telling somebody they cannot format is worse than saying nothing. No
            folders is unchanged and is still worth saying, because it is the
            question a list of notes raises. Where the vocabulary is written down
            is Settings, and the note's own placeholder names two of it - so this
            line does not become a third copy. */}
        {list !== null && list.length > 0 && (
          <p className="finetext">
            No folders here. Pin a note to keep it at the top, or put notes into sections of your
            own. Notes written on reminders and scheduled items are grouped automatically - they
            cannot be pinned or moved, and opening one edits the same note on that item.
          </p>
        )}

        {/* THE WAY TO MAKE A SECTION, and it is at the foot on purpose. Depth is
            reachable and never surfaced (AGENTS.md): somebody who has not
            noticed sections exist should not be asked to organise anything
            before they can read what they wrote, and somebody who wants one
            finds it under the sentence that describes it. */}
        {list !== null && <NewSection onCreate={notes.createSection} />}

        {archivedSections.length > 0 && (
          <div className="setgroup">
            <h2>Deleted sections</h2>
            <div className="setlist">
              {archivedSections.map((section) => (
                <DeletedSectionRow
                  key={section.id}
                  section={section}
                  armed={arming === `sec-${section.id}`}
                  onArm={() => setArming(`sec-${section.id}`)}
                  onDisarm={() => setArming(null)}
                  onRestore={() => void notes.restoreSection(section)}
                  onDeleteForever={() => void notes.deleteSectionForever(section)}
                />
              ))}
            </div>
            {/* The promise the whole feature turns on, said where it matters
                rather than left to be discovered: deleting a section is not a
                way to lose notes, and neither is deleting one for good. */}
            <p className="finetext">
              A deleted section stays here until you delete it for good. The notes that were in it
              are never deleted with it - they are in the list above, in no section - and putting
              the section back puts them all in it again.
            </p>
          </div>
        )}

        {notes.deleted.length > 0 && (
          <div className="setgroup">
            <h2>Deleted</h2>
            <div className="setlist">
              {notes.deleted.map((note) => (
                <DeletedRow
                  key={note.id}
                  note={note}
                  today={notes.today}
                  armed={arming === `note-${note.id}`}
                  onArm={() => setArming(`note-${note.id}`)}
                  onDisarm={() => setArming(null)}
                  onRestore={() => void notes.restore(note)}
                  onDeleteForever={() => void notes.deleteForever(note)}
                />
              ))}
            </div>
            {/* Said plainly rather than implied: nothing sweeps these up, so a
                deleted note is here until somebody says otherwise. Promising an
                expiry no job performs would be the worse of the two. */}
            <p className="finetext">
              A deleted note stays here until you delete it for good. Nothing removes one on its
              own, and deleting for good cannot be undone.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * What is said when the list has nothing on it, which is FOUR sentences and not
 * one.
 *
 * The Deleted group is drawn below this and comes from the same read, so an
 * empty state that ignores it contradicts what is on screen underneath it:
 * "Nothing written down yet." over a list of the things you wrote is simply
 * false, and so is "No notes match." when the search matched a note you deleted
 * this morning - which it may, deliberately, because somebody looking for one
 * should find it where it is rather than be told nothing matches.
 *
 * Drawing NOTHING in those two cases was the other option and is worse: it takes
 * the falsehood away and leaves somebody looking at a Deleted group with nothing
 * above it saying why the list over it is empty. So each state gets a sentence
 * that is true of it, and the two about deleted notes point at the group they
 * are in.
 *
 * The button follows the search rather than the notes: what to do about an empty
 * search is to clear it, and what to do about an empty account is to write
 * something. Both are on screen here rather than named somewhere else, which is
 * the rule this screen already keeps for New note.
 */
function EmptyNotes({
  searching,
  anyDeleted,
  onClear,
  onNew,
}: {
  searching: boolean;
  anyDeleted: boolean;
  onClear: () => void;
  onNew: () => void;
}) {
  // ONE of the four sentences has an approved pair. The other three point at
  // WHERE the notes are - in the Deleted group, or nowhere yet - which is a fact
  // about the account, so they read the same in both voices (`shell/voice.ts`).
  const voice = useVoice();

  if (searching) {
    return (
      <div className="empty">
        <h3>
          {anyDeleted ? 'The only matches are in Deleted.' : wording('empty.notes.title', voice)}
        </h3>
        <p>
          {anyDeleted
            ? 'They are in the group below. Try a different word, or clear the search.'
            : 'Try a different word, or clear the search.'}
        </p>
        <button className="btn" onClick={onClear}>
          Clear search
        </button>
      </div>
    );
  }

  return (
    <div className="empty">
      <h3>
        {anyDeleted ? 'Everything you have written is in Deleted.' : 'Nothing written down yet.'}
      </h3>
      <p>
        {anyDeleted
          ? 'It is all in the group below, and Restore puts any of it back.'
          : 'Anything you write on an item in one of your schedules turns up here too, beside the notes you keep on their own.'}
      </p>
      {/* The same control as the one in the top row, said in the words of the
          thing it makes rather than pointing at a button somewhere else on the
          screen. */}
      <button className="btn primary" onClick={onNew}>
        New note
      </button>
    </div>
  );
}

interface GroupProps {
  group: NoteGroup;
  /** Whether the screen is drawing headings at all. A plain list draws none. */
  grouped: boolean;
  today: IsoDay | null;
  mode: Mode;
  sections: NoteSection[];
  folded: boolean;
  foldable: boolean;
  onToggleFold: () => void;
  dragging: Note | null;
  over: boolean;
  onOver: () => void;
  onLeave: () => void;
  onDrop: (note: Note) => void;
  onDragNote: (note: Note | null) => void;
  onDragEnd: () => void;
  onOpen: (note: Note) => void;
  onPin: (note: Note, pinned: boolean) => void;
  onMove: (note: Note, sectionId: string | null) => void;
  onRename: (name: string) => void;
  onPinSection: (pinned: boolean) => void;
  onRemoveSection: () => void;
}

/**
 * One block of the list: the pinned area, a section, or everything in neither.
 *
 * IT IS A DROP TARGET WHATEVER ITS KIND, including the plain block, because
 * taking a note back OUT of a section has to be as direct as putting it in one -
 * a grouping mechanism you can only ever add to is a trap.
 *
 * `dragging === null` is what decides whether a drop is possible at all, rather
 * than the payload on the `DataTransfer`. Reading that during `dragover` is not
 * allowed in any browser (only its `types` are), so a target that needed the id
 * to decide would have to guess - and the one thing it needs to know is whether
 * what is being dragged is one of this screen's own rows, which is exactly what
 * this state says.
 */
function NoteGroupView({
  group,
  grouped,
  today,
  mode,
  sections,
  folded,
  foldable,
  onToggleFold,
  dragging,
  over,
  onOver,
  onLeave,
  onDrop,
  onDragNote,
  onDragEnd,
  onOpen,
  onPin,
  onMove,
  onRename,
  onPinSection,
  onRemoveSection,
}: GroupProps) {
  const section = group.section;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');

  // A drag can only be received by a block that is not where the note already
  // is. Dropping a note back where it came from is a no-op the model refuses
  // anyway, but a target that lights up for it promises something to look at.
  const takes =
    dragging !== null &&
    group.kind !== 'automatic' &&
    canOrganise(dragging) &&
    (group.kind === 'pinned'
      ? !dragging.pinned
      : dragging.pinned || (dragging.noteSectionId ?? null) !== (section?.id ?? null));

  const body = (
    <div className="ngbody" inert={folded}>
      <div>
        {group.notes.length === 0 ? (
          <p className="ngempty">
            Nothing in here yet. Drag a note onto it, or choose this section on a note.
          </p>
        ) : (
          <div className="notelist">
            {group.notes.map((note) => (
              <NoteRow
                key={`${note.kind}-${note.id}`}
                note={note}
                today={today}
                mode={mode}
                sections={sections}
                dragged={dragging?.id === note.id && dragging.kind === note.kind}
                onOpen={() => onOpen(note)}
                onPin={(pinned) => onPin(note, pinned)}
                onMove={(sectionId) => onMove(note, sectionId)}
                onDragStart={() => onDragNote(note)}
                onDragEnd={onDragEnd}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // The plain block on an account with only free-standing notes, no sections
  // and nothing pinned. It is the list this screen has always drawn, with no
  // heading over it and nothing to collapse - the general-audience default, in
  // one branch.
  if (!grouped) return body;

  return (
    <section
      className={['ngroup', folded ? 'folded' : '', over && takes ? 'dropping' : '']
        .filter(Boolean)
        .join(' ')}
      onDragOver={(event) => {
        if (!takes) return;
        // The default action of `dragover` is to REFUSE the drop, so preventing
        // it is what makes this a target at all. It has to happen on every
        // event, not once.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        onOver();
      }}
      onDragLeave={onLeave}
      onDrop={(event) => {
        if (!takes || dragging === null) return;
        event.preventDefault();
        onDrop(dragging);
      }}
    >
      <div className="nghead">
        {renaming && section !== null ? (
          <form
            className="ngrename"
            onSubmit={(event) => {
              event.preventDefault();
              const name = draft.trim();
              setRenaming(false);
              if (name !== '') onRename(name);
            }}
          >
            <input
              autoFocus
              value={draft}
              maxLength={NAME_MAX}
              aria-label={`Rename ${section.name}`}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setRenaming(false);
                }
              }}
            />
            <button className="btn primary" type="submit">
              Save
            </button>
            <button className="btn" type="button" onClick={() => setRenaming(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <>
            {foldable ? (
              <button
                className="ngfold"
                aria-expanded={!folded}
                aria-label={
                  group.kind === 'automatic'
                    ? `Automatic section ${group.title}, ${group.notes.length} ${group.notes.length === 1 ? 'note' : 'notes'}`
                    : undefined
                }
                onClick={onToggleFold}
                // Its name is its content, which is the block's own name plus
                // its count - so a screen reader reads "Work, 3, collapsed"
                // rather than a verb. What it does is `aria-expanded`'s job.
              >
                <Icon name="chevD" />
                <span className="ngname">{group.title}</span>{' '}
                <span className="ngcount tnum">{group.notes.length}</span>
              </button>
            ) : (
              // No fold control under a search, because nothing is folded under
              // one. A heading rather than a dead button.
              <h2
                className="ngfold static"
                aria-label={
                  group.kind === 'automatic'
                    ? `Automatic section ${group.title}, ${group.notes.length} ${group.notes.length === 1 ? 'note' : 'notes'}`
                    : undefined
                }
              >
                <span className="ngname">{group.title}</span>{' '}
                <span className="ngcount tnum">{group.notes.length}</span>
              </h2>
            )}

            {section !== null && (
              <>
                <button
                  className="ngact"
                  aria-pressed={section.pinned}
                  aria-label={`Keep ${section.name} at the top`}
                  onClick={() => onPinSection(!section.pinned)}
                >
                  <Icon name="pin" />
                </button>
                <button
                  className="ngact"
                  aria-label={`Rename ${section.name}`}
                  onClick={() => {
                    setDraft(section.name);
                    setRenaming(true);
                  }}
                >
                  <Icon name="pencil" />
                </button>
                {/* One press, no confirmation, because this ARCHIVES: the toast
                    carries the Undo and says what happened to the notes. The
                    question is asked once, in the Deleted sections group, on the
                    button that cannot be taken back. */}
                <button
                  className="ngact danger"
                  aria-label={`Delete ${section.name}`}
                  onClick={onRemoveSection}
                >
                  <Icon name="trash" />
                </button>
              </>
            )}
          </>
        )}
      </div>

      {body}
    </section>
  );
}

/** One note on the list, of either kind. */
function NoteRow({
  note,
  today,
  mode,
  sections,
  dragged,
  onOpen,
  onPin,
  onMove,
  onDragStart,
  onDragEnd,
}: {
  note: Note;
  today: IsoDay | null;
  /** Resolved once by `NotesScreen`: an unbounded list mounts one subscription. */
  mode: Mode;
  /** What the picker can offer. Empty on an account that has made none. */
  sections: NoteSection[];
  dragged: boolean;
  onOpen: () => void;
  onPin: (pinned: boolean) => void;
  onMove: (sectionId: string | null) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const preview = previewOf(note.body);
  const where = whereLabel(note);
  // An ITEM note has neither column behind it, so it is drawn with neither
  // control rather than with controls that would be refused (`note-model.ts`).
  const organisable = canOrganise(note);

  return (
    <div
      className={['noterow', dragged ? 'dragged' : ''].filter(Boolean).join(' ')}
      // A note on an item carries its schedule's hue exactly as the card on the
      // day does; `--c` is set here and the tint is derived in CSS, so one
      // declaration is right on both surfaces.
      style={
        note.schedule === null
          ? undefined
          : ({ '--c': hueIn(note.schedule.colorHex, mode) } as CSSProperties)
      }
      draggable={organisable}
      onDragStart={(event) => {
        // Firefox starts no drag at all unless something is set, and the id is
        // the honest thing to carry - though nothing reads it back: the screen
        // holds the row itself, because `dragover` may not read a payload.
        event.dataTransfer.setData('text/plain', note.id);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
    >
      <button className="nopen" onClick={onOpen}>
        <span className="nt">
          <span className="ntx">{note.heading}</span>
          {today !== null && <em>{whenLabel(note.updatedDay, today)}</em>}
        </span>
        {preview !== '' && <span className="np">{preview}</span>}
        {where !== null && (
          <span className="nsrc">
            {note.schedule !== null && <i className="huedot" aria-hidden="true" />}
            {where}
          </span>
        )}
        {/* WHICH OF YOUR NOTES A STRANGER CAN READ, on the list, without opening
            one. Not decoration: people read "unlisted" as "private", so the one
            thing a published note owes its owner is to look different from the
            rest. It reuses the row that says where an ITEM note lives, which
            costs no second line - the two can never both appear, because an item
            note cannot be shared at all. */}
        {note.shared && (
          <span className="nsrc nshared">
            <Icon name="link" />
            Shared with a link
          </span>
        )}
      </button>

      {organisable && (
        <button
          className="npin"
          aria-pressed={note.pinned}
          // Named for the note it acts on: a list of rows makes a list of
          // buttons whose visible content is the same glyph, and "Pin, Pin, Pin"
          // says which control exists and not which note it holds down.
          aria-label={`Pin ${note.heading} to the top`}
          onClick={() => onPin(!note.pinned)}
        >
          <Icon name="pin" />
        </button>
      )}

      {/* THE NON-DRAG PATH, and a real `<select>` rather than a menu of this
          client's own: it is reachable by keyboard, announced properly, and on a
          phone it opens the platform's own picker - which matters more here than
          anywhere else on this screen, because HTML5 drag and drop does not fire
          on a touch screen at all.

          Only drawn once a section exists. An account that has made none has
          nothing to choose between, and a picker offering one option is an empty
          organiser on a screen that is supposed to be a list of notes. */}
      {organisable && sections.length > 0 && (
        <span className="nsecwrap">
          <select
            className="nsec"
            aria-label={`Section for ${note.heading}`}
            value={note.noteSectionId ?? ''}
            onChange={(event) => onMove(event.target.value === '' ? null : event.target.value)}
          >
            <option value="">No section</option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </select>
          <Icon name="chevD" />
        </span>
      )}
    </div>
  );
}

/**
 * The way to make a section: a button that becomes a field, and nothing until
 * it is pressed.
 *
 * A field standing open on the Notes screen of an account with three notes is
 * the empty organiser this feature was told not to build. The button is one
 * line of quiet text until somebody wants it.
 */
function NewSection({ onCreate }: { onCreate: (name: string) => Promise<boolean> }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  if (!adding) {
    return (
      <div className="newsec">
        <button className="btn" onClick={() => setAdding(true)}>
          <Icon name="plus" />
          New section
        </button>
        <p className="finetext">
          A section is a heading you invent - Work, Ideas, anything - with your notes grouped under
          it. There is no limit on them, and deleting one never deletes the notes inside it.
        </p>
      </div>
    );
  }

  return (
    <div className="newsec">
      <form
        className="ngrename"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed === '') return;
          void (async () => {
            // The field is cleared only when the section was actually made. A
            // form that emptied itself on a failure would take the typed name
            // with it, and the toast saying it failed would be about something
            // nobody can try again without retyping.
            if (await onCreate(trimmed)) {
              setName('');
              setAdding(false);
            }
          })();
        }}
      >
        <input
          autoFocus
          value={name}
          maxLength={NAME_MAX}
          placeholder="Work"
          aria-label="Name for the new section"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setAdding(false);
            }
          }}
        />
        <button className="btn primary" type="submit">
          Add
        </button>
        <button className="btn" type="button" onClick={() => setAdding(false)}>
          Cancel
        </button>
      </form>
    </div>
  );
}

/**
 * A deleted note, and the two ways out of that state.
 *
 * Not a `button.setrow` holding more buttons - a row that is itself a button
 * cannot hold another one, and there is nothing to open here anyway: a deleted
 * note is put back first.
 */
function DeletedRow({
  note,
  today,
  armed,
  onArm,
  onDisarm,
  onRestore,
  onDeleteForever,
}: {
  note: Note;
  today: IsoDay | null;
  /** Whether THIS row is the one being asked about. Owned by the screen. */
  armed: boolean;
  onArm: () => void;
  onDisarm: () => void;
  onRestore: () => void;
  onDeleteForever: () => void;
}) {
  return (
    <div className="setrow notedel">
      <span className="st">
        <b>{note.heading}</b>
        <span>
          {today === null
            ? 'Deleted'
            : `Deleted - last changed ${whenLabel(note.updatedDay, today)}`}
        </span>
      </span>
      {/* Named for the note each one acts on: several deleted notes make
          several buttons whose visible word is the same, and "Restore, Restore,
          Restore" says which button exists and not which note it puts back. */}
      <button className="btn" aria-label={`Restore ${note.heading}`} onClick={onRestore}>
        Restore
      </button>
      {armed ? (
        <>
          {/* The way OUT of the question, which a confirm without one is not:
              somebody who armed this by mistake has otherwise only the button
              that does it. */}
          <button className="btn" aria-label={`Keep ${note.heading}`} onClick={onDisarm}>
            Cancel
          </button>
          <button
            className="btn danger"
            aria-label={`Yes, delete ${note.heading} for good`}
            onClick={onDeleteForever}
          >
            Yes, for good
          </button>
          {/* Said on the row that is armed, and only while it is. The fine print
              at the foot of the group is always up, so it says nothing about
              WHICH note is about to go - and this is the one act with no undo. */}
          <p className="delwarn" role="alert">
            &quot;{note.heading}&quot; will be gone, and this one cannot be undone.
          </p>
        </>
      ) : (
        <button
          className="btn danger"
          aria-label={`Delete ${note.heading} for good`}
          onClick={onArm}
        >
          Delete for good
        </button>
      )}
    </div>
  );
}

/**
 * A deleted section, and the two ways out of that state.
 *
 * The same shape as `DeletedRow` above, and deliberately so - one pattern for
 * "this is gone, here is the way back, and here is the way to finish it" - but
 * with a different warning, because what is at stake is different. Deleting a
 * NOTE for good destroys writing. Deleting a SECTION for good destroys a
 * heading, and the notes under it survive it: that is worth saying at the moment
 * the red button is armed, since somebody who thinks otherwise will not press
 * it and will keep a section they do not want.
 */
function DeletedSectionRow({
  section,
  armed,
  onArm,
  onDisarm,
  onRestore,
  onDeleteForever,
}: {
  section: NoteSection;
  armed: boolean;
  onArm: () => void;
  onDisarm: () => void;
  onRestore: () => void;
  onDeleteForever: () => void;
}) {
  return (
    <div className="setrow notedel">
      <span className="st">
        <b>{section.name}</b>
        <span>Deleted - the notes that were in it are safe</span>
      </span>
      <button className="btn" aria-label={`Restore ${section.name}`} onClick={onRestore}>
        Restore
      </button>
      {armed ? (
        <>
          <button className="btn" aria-label={`Keep ${section.name}`} onClick={onDisarm}>
            Cancel
          </button>
          <button
            className="btn danger"
            aria-label={`Yes, delete ${section.name} for good`}
            onClick={onDeleteForever}
          >
            Yes, for good
          </button>
          <p className="delwarn" role="alert">
            &quot;{section.name}&quot; will be gone and cannot be brought back. The notes that were
            in it stay, in no section.
          </p>
        </>
      ) : (
        <button
          className="btn danger"
          aria-label={`Delete ${section.name} for good`}
          onClick={onArm}
        >
          Delete for good
        </button>
      )}
    </div>
  );
}
